<template>
  <div class="auth-container" :style="containerStyle">
    <div class="auth-box card">
      <!-- Dynamic Logo -->
      <div class="login-logo" v-if="brandingLogo && !logoError">
        <img :src="brandingLogo" :alt="organizationName" @error="handleLogoError" />
      </div>
      <div v-else-if="logoError" class="login-logo-text">
        <h1>{{ organizationName }}</h1>
      </div>
      <h2 class="text-center">{{ loginTitle }}</h2>
      <!-- OAuth Login Options -->
      <div class="oauth-section">
        <OAuthButtons
          :organization-slug="organizationSlug"
          :show-linked-in="true"
          @success="onOAuthSuccess"
          @error="onOAuthError"
        />
        <p v-if="isInviteOnlyLogin" class="oauth-invite-hint">
          Use Google or Microsoft if you already have an account. New users need an admin invite.
        </p>

        <div class="divider">
          <span>or continue with email</span>
        </div>
      </div>

      <!-- Traditional Email/Password Login -->
      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <label for="username">Email Address</label>
          <input type="text" id="username" v-model="username" class="form-control" required autocomplete="username">
        </div>
        <div class="form-group">
          <label for="password">Password</label>
          <input type="password" id="password" v-model="password" class="form-control" required autocomplete="current-password">
        </div>
        <div class="forgot-password-link">
          <router-link :to="forgotPasswordRoute">Forgot your password?</router-link>
        </div>
        <div v-if="error" class="error-message">{{ error }}</div>
        <button type="submit" class="btn btn-block" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ loading ? 'Logging in...' : 'Login' }}
        </button>
      </form>
      
      <!-- Organization-specific registration and links -->
      <div v-if="showPortalLinks" class="scav-links-section">
        <template v-if="isVmaeDemoOrg">
          <div class="scav-link-item">
            <span>Don't have an account?</span>
            <router-link :to="registerRoute" class="scav-link">Create Account</router-link>
          </div>
          <div v-if="associationWebsite" class="scav-link-item">
            <span>Not a member?</span>
            <a :href="associationWebsite" target="_blank" rel="noopener noreferrer" class="scav-link">
              Visit association website
            </a>
          </div>
        </template>
        <template v-else>
          <div class="scav-link-item">
            <span>Not a member?</span>
            <a href="https://scav.memberclicks.net/index.php?option=com_mcform&view=ngforms&id=2056696#/" target="_blank" class="scav-link">Join SCAV</a>
          </div>
          <div class="scav-link-item">
            <span>Don't have an account?</span>
            <router-link :to="registerRoute" class="scav-link">Create Account</router-link>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import auth from '@/services/auth';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import OAuthButtons from '@/components/OAuthButtons.vue';
import {
  isLearningPortalOrg,
  isVmaeDemoOrgSlug,
  learningHomeLocation,
  resolveOrgSlugFromHost,
} from '@/utils/associationPortal';

export default {
  name: 'LoginView',
  components: { OAuthButtons },
  data() {
    return {
      username: '',
      password: '',
      loading: false,
      error: '',
      organizationSlug: null,
      branding: null,
      logoError: false,
    };
  },
  
  async mounted() {
    // Get organization from URL parameter or detect from domain
    this.organizationSlug = this.$route.query.org || resolveOrgSlugFromHost();
    
    if (this.organizationSlug) {
      try {
        // Load branding for this organization
        await whitelabelBranding.loadOrganizationBranding(this.organizationSlug);
        this.branding = whitelabelBranding.getCurrentBranding();
        console.log('✅ Loaded branding for', this.organizationSlug);
      } catch (error) {
        console.error('⚠️ Failed to load org branding:', error);
        // Continue with default branding
      }
    }
  },
  
  computed: {
    brandingLogo() {
      return whitelabelBranding.getLogo();
    },
    
    organizationName() {
      return whitelabelBranding.getAppName();
    },
    
    loginTitle() {
      if (this.branding && this.branding.app_name) {
        return `Sign in to ${this.branding.app_name}`;
      }
      return 'Login';
    },
    
    containerStyle() {
      const primaryColor = whitelabelBranding.getColor('primary');
      const secondaryColor = whitelabelBranding.getColor('secondary');
      const styles = { '--login-primary': primaryColor };

      if (this.isVmaeDemoOrg || this.organizationSlug === 'colibri-media-group') {
        styles.background = `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
        styles.minHeight = '100vh';
      }

      return styles;
    },
    
    forgotPasswordRoute() {
      if (this.organizationSlug) {
        return `/forgot-password?org=${this.organizationSlug}`;
      }
      return '/forgot-password';
    },

    isInviteOnlyLogin() {
      return !this.showPortalLinks;
    },

    showPortalLinks() {
      const host = window.location.hostname.toLowerCase();
      const isVetMediaDomain = host.includes('vetmediagroup.com')
        || host.includes('scav.org')
        || host === 'veterinarymedia.ai'
        || host.endsWith('.veterinarymedia.ai');
      const isSCAVOrg = this.organizationSlug === 'colibri-media-group';
      const isVmaeDemo = isVmaeDemoOrgSlug(this.organizationSlug);

      return isVetMediaDomain || isSCAVOrg || isVmaeDemo;
    },

    isVmaeDemoOrg() {
      return isVmaeDemoOrgSlug(this.organizationSlug);
    },

    associationWebsite() {
      return whitelabelBranding.getCurrentBranding()?.association_meta?.website || '';
    },

    registerRoute() {
      if (this.organizationSlug) {
        return `/register?org=${encodeURIComponent(this.organizationSlug)}`;
      }
      return '/register';
    },
  },
  
  methods: {
    handleLogoError() {
      console.warn('Logo failed to load, falling back to text');
      this.logoError = true;
    },
    async handleLogin() {
      this.loading = true;
      this.error = '';
      try {
        await auth.login(this.username, this.password, this.organizationSlug);
        
        // Reload branding after successful login based on user's organization
        await this.reloadBrandingAfterLogin();
        
        // Redirect based on user role and organization
        const defaultRoute = this.getDefaultRoute();
        this.$router.push(this.$route.query.redirect || defaultRoute);
      } catch (error) {
        if (error.response && error.response.data) {
          this.error = error.response.data.detail || 'Failed to login. Please check your credentials.';
        } else {
          this.error = 'An unexpected error occurred. Please try again.';
        }
        console.error('Login failed:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async reloadBrandingAfterLogin() {
      try {
        // Get the logged-in user's organization
        const user = auth.getUser();
        if (user && user.organization && user.organization.slug) {
          console.log('🎨 Reloading branding for organization:', user.organization.slug);
          await whitelabelBranding.loadOrganizationBranding(user.organization.slug);
        }
      } catch (error) {
        console.warn('Failed to reload branding after login:', error);
        // Continue - app will use default branding
      }
    },

    getDefaultRoute() {
      const userRole = auth.getUserRole();
      const user = auth.getUser();
      const orgSlug = user?.organization?.slug || this.organizationSlug;
      
      console.log('🏁 Determining default route for:', { orgSlug, userRole });

      if (isLearningPortalOrg(orgSlug)) {
        return learningHomeLocation(orgSlug);
      }
      
      // Roles that default to Learning Catalog
      // SPEAKER is a CE faculty role — they live entirely in the LMS,
      // so route them straight to the catalog instead of the (forbidden)
      // Decision Center fallback.
      if (userRole === 'STUDENT' || userRole === 'LEARNER' || userRole === 'SPEAKER') {
        return learningHomeLocation(orgSlug);
      }
      
      // Portfolio hub is the canonical post-login landing for compliance roles.
      return '/clients';
    },

    // OAuth sign-in is handled by the shared <OAuthButtons> component, which
    // performs the provider token exchange and stores the JWT via the auth
    // service. We just reload branding and route on success.
    async onOAuthSuccess() {
      try {
        await this.reloadBrandingAfterLogin();
        const defaultRoute = this.getDefaultRoute();
        this.$router.push(this.$route.query.redirect || defaultRoute);
      } catch (error) {
        console.error('Post-OAuth navigation failed:', error);
        this.error = 'Signed in, but failed to load your workspace. Please refresh.';
      }
    },

    onOAuthError(message) {
      this.error = message || 'Sign-in failed. Please try again.';
    },
  },
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-surface);
  padding: 1.5rem;
}
.auth-box {
  width: 100%;
  max-width: 400px;
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}
.text-center {
  text-align: center;
}
.mt-3 {
  margin-top: var(--spacing-md);
}
label {
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
}

/* OAuth Styling */
.oauth-section {
  margin-bottom: 2rem;
}

.oauth-invite-hint {
  margin: 0.75rem 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
  text-align: center;
}

.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid #e1e5e9;
  border-radius: var(--border-radius);
  background: white;
  color: #333;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.oauth-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.oauth-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.oauth-btn i {
  font-size: 1.1rem;
}

/* Whitelabel Logo Styling */
.login-logo {
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo img {
  max-width: 250px;
  max-height: 80px;
  object-fit: contain;
}

.login-logo-text {
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo-text h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--login-primary, var(--color-primary, #1E40AF));
  margin: 0;
}

/* Use custom primary color from branding */
.btn-primary,
.btn-block {
  background-color: var(--login-primary, var(--color-primary, #1E40AF));
}

.btn-primary:hover,
.btn-block:hover {
  background-color: var(--login-primary, var(--color-primary, #1E40AF));
  filter: brightness(110%);
}

/* Provider-specific colors */
.google-btn:hover:not(:disabled) {
  border-color: #4285f4;
  color: #4285f4;
}

.microsoft-btn:hover:not(:disabled) {
  border-color: #0078d4;
  color: #0078d4;
}

.linkedin-btn:hover:not(:disabled) {
  border-color: #0a66c2;
  color: #0a66c2;
}

.apple-btn:hover:not(:disabled) {
  border-color: #000;
  color: #000;
}

.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e1e5e9;
}

.divider span {
  background: white;
  padding: 0 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Forgot Password Link */
.forgot-password-link {
  text-align: right;
  margin-bottom: 1rem;
  margin-top: -0.5rem;
}

.forgot-password-link a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.forgot-password-link a:hover {
  color: var(--login-primary, var(--color-primary, #1E40AF));
  text-decoration: underline;
}

/* SCAV-specific links section */
.scav-links-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e1e5e9;
  text-align: center;
}

.scav-link-item {
  margin: 0.75rem 0;
  font-size: 0.95rem;
  color: #6b7280;
}

.scav-link-item span {
  margin-right: 0.5rem;
}

.scav-link {
  color: #5b7ce6;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.scav-link:hover {
  color: #4a63c4;
  text-decoration: underline;
}

.scav-link-item a[target="_blank"]::after {
  content: " ↗";
  font-size: 0.8em;
  opacity: 0.7;
}
</style> 